import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DocumentResp } from './create';
import { uploadFetch } from '@services/api';
import { DocumentsQueryType, getDocumentsQueries, getDocumentsUrl } from '.';
import { useJobRefetch } from '@hooks/job/refetch';
import { mutationKeys } from '@utils/consts';
import { trackedUpload } from '@hooks/useUploadQueue';
import { useTranslation } from 'react-i18next';

export const useUpdateDocument = ({
  callback,
  queryType,
}: {
  callback: (data: DocumentResp) => void;
  queryType: DocumentsQueryType;
}) => {
  const { refetch } = useJobRefetch();
  const queryClient = useQueryClient();
  const { t } = useTranslation('app');

  const { mutate, isPending } = useMutation({
    mutationKey: mutationKeys.fileUpload,
    mutationFn: ({ id, body }: { id?: string; body: FormData }): Promise<DocumentResp> =>
      trackedUpload(body, t('fileUpload.title'), (onProgress) =>
        uploadFetch({
          url: `${getDocumentsUrl(queryType)}/${id}`,
          method: 'PUT',
          body,
          onProgress,
        }),
      ),
    onError: (err) => {
      console.error(err);
    },
    onSuccess: async ({ success }) => {
      if (!success) return;
      if (queryType === 'documents' || queryType === 'subtask') await refetch();
      else {
        getDocumentsQueries(queryType).forEach(
          (queryKey) => void queryClient.refetchQueries({ queryKey }),
        );
      }
    },
    onSettled: (data) => {
      if (data) callback(data);
    },
  });

  return {
    mutate,
    isLoading: isPending,
  };
};
